import React, { useState, useRef } from 'react';
import {
  OutlinedInput,
  InputAdornment,
  Popover,
  Grid,
  Button,
  Typography,
} from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import useStyles from './styles';

const MESES_PT_BR = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

const MESES_ABREV = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

function formatMonthYear(date) {
  if (!date) return '';
  const month = date.getMonth();
  const year = date.getFullYear();
  return `${MESES_PT_BR[month]} de ${year}`;
}

const ANO_MIN = 2021;
const ANO_MAX = new Date().getFullYear();

function buildYears() {
  const list = [];
  for (let y = ANO_MIN; y <= ANO_MAX; y += 1) list.push(y);
  return list;
}

const YEARS = buildYears();

export default function MonthPicker({
  value, onChange, className, ...rest
}) {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'year'
  const [pendingMonth, setPendingMonth] = useState(null); // after selecting month, go to year

  const displayValue = value ? formatMonthYear(value) : '';
  const currentYear = value ? value.getFullYear() : new Date().getFullYear();
  const currentMonth = value ? value.getMonth() : new Date().getMonth();
  const viewYear = value ? value.getFullYear() : new Date().getFullYear();

  const handleOpen = () => {
    setViewMode('month');
    setPendingMonth(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setViewMode('month');
    setPendingMonth(null);
  };

  const handleSelectMonth = (monthIndex) => {
    setPendingMonth(monthIndex);
    setViewMode('year');
  };

  const handleSelectYear = (year) => {
    const month = pendingMonth !== null ? pendingMonth : currentMonth;
    onChange(new Date(year, month, 1));
    handleClose();
  };

  return (
    <>
      <OutlinedInput
        ref={anchorRef}
        value={displayValue}
        readOnly
        onClick={handleOpen}
        className={className || classes.root}
        classes={{
          notchedOutline: classes.inputOutline,
          input: classes.input,
        }}
        inputProps={{
          'aria-label': 'Selecionar mês e ano',
          readOnly: true,
        }}
        endAdornment={(
          <InputAdornment position="end">
            <CalendarTodayIcon className={classes.calendarIcon} fontSize="small" />
          </InputAdornment>
        )}
        {...rest}
      />
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.popoverPaper }}
        disablePortal
      >
        {viewMode === 'month' && (
          <>
            <div className={classes.header}>
              <Typography variant="subtitle2" color="textSecondary">
                Selecione o mês
              </Typography>
            </div>
            <Grid container spacing={1} className={classes.gridContainer}>
              {MESES_ABREV.map((nome, i) => (
                <Grid item xs={4} key={nome}>
                  <Button
                    fullWidth
                    size="small"
                    className={
                      viewYear === currentYear && i === currentMonth
                        ? `${classes.monthButton} ${classes.monthButtonSelected}`
                        : classes.monthButton
                    }
                    onClick={() => handleSelectMonth(i)}
                  >
                    {nome}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {viewMode === 'year' && (
          <>
            <div className={classes.header}>
              <Typography variant="subtitle2" color="textSecondary">
                {pendingMonth !== null ? MESES_PT_BR[pendingMonth] : 'Selecione o ano'}
              </Typography>
            </div>
            <Grid container spacing={1} className={classes.gridContainer}>
              {YEARS.map((y) => (
                <Grid item xs={4} key={y}>
                  <Button
                    fullWidth
                    size="small"
                    className={
                      y === currentYear
                        ? `${classes.yearButton} ${classes.yearButtonSelected}`
                        : classes.yearButton
                    }
                    onClick={() => handleSelectYear(y)}
                  >
                    {y}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Popover>
    </>
  );
}
